import { Eye, PencilLine, Receipt, ShieldAlert } from 'lucide-react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { BomPanel } from '@/components/projects/bom-panel'
import { OutlineEditor } from '@/components/projects/outline-editor'
import { OutlineBasic } from '@/components/projects/outline-basic'
import { OutlineStyleToggle } from '@/components/projects/outline-style-toggle'
import { ProjectHeader } from '@/components/projects/project-header'
import { ReferenceThisButton } from '@/components/projects/reference-this-button'
import { DoingView } from '@/components/doer/doing-view'
import { StartProjectButton } from '@/components/doer/start-project-button'
import { useProjectByHandleSlug, useProjectDetail } from '@/features/projects/use-projects'
import { useFollowingDetail } from '@/features/doer/use-doer'
import { useOutlineStyle, type OutlineStyle } from '@/features/projects/use-outline-style'
import { useMe } from '@/features/users/use-users'
import { useIsMobile } from '@/lib/use-is-mobile'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import { formatUsd, rollupBom } from '@/features/projects/rollup-bom'
import { useEffect } from 'react'
import type { ProjectDetail } from '@/api/types'

const MobileBomTrigger = ({ project }: { project: ProjectDetail }) => {
  const rollup = rollupBom(project)
  const total =
    rollup.materials.length === 0 && rollup.tools.length === 0
      ? 'Bill of materials'
      : `Bill of materials · ${formatUsd(rollup.grandTotalCents)}`
  return (
    <div className="lg:hidden">
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="default"
            className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2 gap-2 shadow-lg"
          >
            <Receipt className="h-4 w-4" />
            {total}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Bill of materials</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <BomPanel project={project} variant="plain" />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

const ProjectScaffold = ({
  project,
  readOnly,
  modeToggle,
  startCta,
  outlineStyle = 'detailed',
}: {
  project: ProjectDetail
  readOnly: boolean
  modeToggle?: React.ReactNode
  startCta?: React.ReactNode
  /**
   * Which outline renderer to use. Defaults to `detailed` (the existing rich
   * `OutlineEditor`). The viewer branch can swap to `basic` for a minimal indented-title
   * skim that's friendlier on large outlines.
   */
  outlineStyle?: OutlineStyle
}) => (
  <>
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        {(startCta || modeToggle) && (
          <div className="flex flex-wrap items-center justify-end gap-3 rounded-md border bg-muted/30 p-3 text-sm">
            {modeToggle}
            {startCta && (
              <>
                <span className="text-muted-foreground">Want to build this?</span>
                {startCta}
              </>
            )}
          </div>
        )}
        {outlineStyle === 'basic' ? (
          <OutlineBasic projectId={project.id} steps={project.steps} />
        ) : (
          <OutlineEditor
            projectId={project.id}
            steps={project.steps}
            catalog={project.catalog}
            readOnly={readOnly}
          />
        )}
      </div>
      <div className="hidden space-y-4 lg:block">
        <BomPanel project={project} />
      </div>
    </div>
    <MobileBomTrigger project={project} />
  </>
)

const LoadingScaffold = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <Skeleton className="h-64" />
      <Skeleton className="h-48" />
    </div>
  </div>
)

const NotFoundView = () => (
  <Card className="mx-auto max-w-lg">
    <CardHeader>
      <CardTitle>Project not found</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <p className="text-sm text-muted-foreground">
        We couldn&apos;t find a project at that URL. It may have been deleted or renamed.
      </p>
      <Button asChild variant="outline">
        <Link to="/dashboard">Back to dashboard</Link>
      </Button>
    </CardContent>
  </Card>
)

const ForbiddenView = () => (
  <Card className="mx-auto max-w-lg">
    <CardHeader className="flex-row items-center gap-2 space-y-0">
      <ShieldAlert className="h-5 w-5 text-destructive" />
      <CardTitle>Private project</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <p className="text-sm text-muted-foreground">
        This project is private. Ask the author to share it with you.
      </p>
      <Button asChild variant="outline">
        <Link to="/dashboard">Back to dashboard</Link>
      </Button>
    </CardContent>
  </Card>
)

const QueryErrorView = ({ error }: { error: unknown }) => {
  const navigate = useNavigate()
  const status = (error as { status?: number })?.status
  if (status === 404) return <NotFoundView />
  if (status === 403) return <ForbiddenView />
  return (
    <Alert variant="destructive">
      <AlertTitle>Failed to load project</AlertTitle>
      <AlertDescription className="flex items-center justify-between gap-4">
        <span>Something went wrong loading the project.</span>
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
          Back to dashboard
        </Button>
      </AlertDescription>
    </Alert>
  )
}

/**
 * Three-way render on the same URL (UI_PLAN §3, §4):
 *
 * <ul>
 *   <li><b>Editor</b> — author's default view; structural edits.
 *   <li><b>Doing</b> — viewer has a Following for this project; mark progress, see personalized BOM.
 *       Authors can also start their own projects, so this branch can fire for them too.
 *   <li><b>Viewer</b> — read-only outline + BOM. Signed-in users (including the author when they
 *       opt in via {@code ?mode=view}) see a "Start this project" CTA.
 * </ul>
 *
 * <p>URL mode overrides:
 *
 * <ul>
 *   <li>{@code ?mode=view} forces the Viewer (lets an author preview their own project as a Doer
 *       would, or click the Start CTA without abandoning a Following).
 *   <li>{@code ?mode=edit} forces the Editor for the author (lets a Doer-author switch back to
 *       editing without abandoning their progress).
 * </ul>
 *
 * <p>Defaults when no mode is set: Following → Doing; else author → Editor; else Viewer.
 */
const ProjectDetailView = ({ data }: { data: ProjectDetail }) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { data: me } = useMe()
  const isMobile = useIsMobile()
  // Phase 19 — viewer-mode outline style toggle. Must be at the top of the component,
  // before any conditional returns, to satisfy rules-of-hooks (we only use it on the
  // viewer branch below, but rules-of-hooks requires unconditional calls).
  const [outlineStyle, setOutlineStyle] = useOutlineStyle()

  const isSignedIn = Boolean(me)
  const isAuthor = Boolean(me && me.handle === data.author.handle)
  const followingQuery = useFollowingDetail(data.id, isSignedIn)
  const isFollowing = followingQuery.data !== undefined
  const mode = searchParams.get('mode')

  const renderEditor = mode === 'edit' && isAuthor
  const renderViewer = mode === 'view'
  const renderDoing = !renderEditor && !renderViewer && isFollowing && followingQuery.data
  const renderEditorDefault = !renderEditor && !renderViewer && !isFollowing && isAuthor

  if (renderDoing) {
    return <DoingView following={followingQuery.data!} viewerIsAuthor={isAuthor} />
  }

  if (renderEditor || renderEditorDefault) {
    const editable = isAuthor && !isMobile
    const showMobileNotice = isAuthor && isMobile
    const previewToggle = (
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => navigate(canonicalProjectUrl(data.author.handle, data.slug, '?mode=view'))}
      >
        <Eye className="h-4 w-4" />
        Preview as a doer
      </Button>
    )
    return (
      <div className="space-y-6">
        <ProjectHeader project={data} readOnly={!editable} />
        {showMobileNotice && (
          <Alert>
            <AlertTitle>Read-only on this device</AlertTitle>
            <AlertDescription>
              The project editor is desktop-only. Open this URL on a wider screen to edit.
            </AlertDescription>
          </Alert>
        )}
        <ProjectScaffold project={data} readOnly={!editable} modeToggle={previewToggle} />
      </div>
    )
  }

  // Viewer branch.
  const showStartCta = isSignedIn && !isFollowing
  // Phase 6: signed-in non-author viewers on a PUBLIC project see "Reference this in one of my
  // projects". UNLISTED + PRIVATE viewer surfaces don't show it (the source isn't insertable
  // cross-author anyway, per the Phase 5/6 visibility rules).
  const showReferenceThis = isSignedIn && !isAuthor && data.visibility === 'PUBLIC'
  const viewerToggle = (
    <div className="flex items-center gap-2">
      <OutlineStyleToggle style={outlineStyle} onChange={setOutlineStyle} />
      {isAuthor && (
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(canonicalProjectUrl(data.author.handle, data.slug))}
        >
          <PencilLine className="h-4 w-4" />
          Back to editor
        </Button>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      <ProjectHeader project={data} readOnly={true} />
      {showReferenceThis && me?.handle && (
        <div className="flex justify-end">
          <ReferenceThisButton
            source={data}
            sourceAuthorHandle={data.author.handle}
            viewerHandle={me.handle}
          />
        </div>
      )}
      <ProjectScaffold
        project={data}
        readOnly={true}
        outlineStyle={outlineStyle}
        modeToggle={viewerToggle}
        startCta={
          showStartCta ? (
            <StartProjectButton
              projectId={data.id}
              authorHandle={data.author.handle}
              slug={data.slug}
            />
          ) : undefined
        }
      />
    </div>
  )
}

/** Phase 6: canonical Project URL `/p/:handle/:slug`. The URL the frontend always generates. */
export const ProjectDetailPage = () => {
  const { handle, slug } = useParams<{ handle: string; slug: string }>()
  const { data, isLoading, isError, error } = useProjectByHandleSlug(handle, slug)

  if (isLoading) return <LoadingScaffold />
  if (isError) return <QueryErrorView error={error} />
  if (!data) return null

  return <ProjectDetailView data={data} />
}

/**
 * Phase 6: legacy `/projects/:id` URL. Fetches by id, then `router.replace`s to the canonical
 * `/p/:handle/:slug` URL preserving any `?mode=` overrides. Existing Phase 1–5 link shares keep
 * working; the address bar updates to canonical form so subsequent navigations are clean.
 */
export const LegacyProjectRedirect = () => {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useProjectDetail(id)

  useEffect(() => {
    if (data) {
      navigate(canonicalProjectUrl(data.author.handle, data.slug, searchParams.toString()), {
        replace: true,
      })
    }
  }, [data, navigate, searchParams])

  if (isLoading || data) return <LoadingScaffold />
  if (isError) return <QueryErrorView error={error} />
  return null
}
